/**
 * BackupSetTable (Spec 201 / 205 UX consistency) — an inline, searchable TABLE of restore
 * backup sets (lakehouse + warehouse), mirroring the Workspace-Complete DR list so BOTH restore
 * flows look and behave the same: a search / type / date-window filter bar on top, then a
 * content-sized DataTable you click to select. Replaces the old modal dropdown picker.
 *
 * Per set it shows: workspace (names repeat across workspaces), type, how many backups, and the
 * date RANGE (oldest→newest). A date window asks the backend how many points fall inside it, so
 * the operator can confidently pick the set that actually holds the data they need. Built from
 * @/components/ui per the design blueprint.
 *
 * Author: Peter Dobler (Dobler Data Solutions) · Feature 201/205
 */
import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Database, Warehouse, CheckCircle2 } from 'lucide-react';
import {
  DataTable,
  type DataTableColumn,
  Input,
  Select,
  Button,
  Badge,
  Checkbox,
  FilterBar,
  EmptyState,
  LoadingState,
  ErrorState,
} from '@/components/ui';
import { restoreApi, BackupSetSummary, BackupEntityType } from '../../api/restore';
import { textMatch } from '../common/ListFilterBar';
import { formatEasternDate } from '@/utils/dateFormatter';

const envVariant: Record<string, 'success' | 'info' | 'warning' | 'default'> = {
  prod: 'success',
  dev: 'info',
  uat: 'warning',
  test: 'default',
};
const iconFor = (t: BackupEntityType) => (t === 'warehouse' ? Warehouse : Database);
const fmtDate = (iso: string | null) =>
  iso ? formatEasternDate(iso) : '—';
const daysAgo = (iso: string | null): number | null =>
  iso ? Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000) : null;
const toDayInput = (d: Date) => d.toISOString().slice(0, 10);

export interface BackupSetTableProps {
  value: { entityType: BackupEntityType; entityId: string } | null;
  onChange: (s: BackupSetSummary) => void;
}

export const BackupSetTable: React.FC<BackupSetTableProps> = ({ value, onChange }) => {
  const [q, setQ] = useState('');
  const [type, setType] = useState(''); // '' | 'lakehouse' | 'warehouse'
  const [from, setFrom] = useState(''); // yyyy-mm-dd
  const [to, setTo] = useState('');
  const [onlyInRange, setOnlyInRange] = useState(true);

  const fromISO = from ? new Date(`${from}T00:00:00`).toISOString() : undefined;
  const toISO = to ? new Date(`${to}T23:59:59`).toISOString() : undefined;
  const hasWindow = !!(from || to);

  const setsQ = useQuery({
    queryKey: ['backup', 'backup-sets', fromISO ?? null, toISO ?? null],
    queryFn: () => restoreApi.backupSets(fromISO, toISO),
  });
  // Workspaces have their own Workspace-Complete DR panel — exclude them so selecting one can't
  // try to load lakehouse recovery points (which would error). And this is the RESTORE screen, so
  // only show sets that actually HAVE backups — a set with 0 recovery points has nothing to restore.
  const sets = (setsQ.data ?? [])
    .filter((s) => (s.entityType as string) !== 'workspace')
    .filter((s) => s.backupCount > 0);

  const rows = useMemo(
    () =>
      [...sets]
        .sort(
          (a, b) =>
            (a.workspaceName ?? '').localeCompare(b.workspaceName ?? '') ||
            (a.name ?? '').localeCompare(b.name ?? '')
        )
        .filter((s) => !type || s.entityType === type)
        .filter((s) => textMatch(q, s.name, s.workspaceName, s.entityType, s.envHint))
        .filter((s) => !hasWindow || !onlyInRange || s.pointsInRange > 0),
    [sets, type, q, hasWindow, onlyInRange]
  );

  // Quick presets set a "between today-N and today-M" window (e.g. 7–14 days ago).
  const presetAgo = (loDays: number, hiDays: number) => {
    const now = Date.now();
    setFrom(toDayInput(new Date(now - hiDays * 86_400_000)));
    setTo(toDayInput(new Date(now - loDays * 86_400_000)));
  };
  const clearWindow = () => {
    setFrom('');
    setTo('');
  };

  const isSel = (s: BackupSetSummary) =>
    value?.entityType === s.entityType && value?.entityId === s.entityId;

  const columns: DataTableColumn<BackupSetSummary>[] = [
    {
      key: 'sel',
      header: '',
      width: '2.5rem',
      sortable: false,
      render: (s) => (isSel(s) ? <CheckCircle2 className="h-4 w-4 text-[var(--color-accent)]" /> : null),
    },
    {
      key: 'name',
      header: 'Name',
      wrap: true,
      sortValue: (s) => s.name ?? '',
      render: (s) => {
        const Icon = iconFor(s.entityType);
        return (
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4 shrink-0 text-[var(--color-text-secondary)]" />
            <span className="font-medium text-[var(--color-text-primary)]">{s.name ?? s.entityId}</span>
            {s.envHint && <Badge variant={envVariant[s.envHint] ?? 'default'}>{s.envHint}</Badge>}
          </span>
        );
      },
    },
    {
      key: 'workspaceName',
      header: 'Workspace',
      wrap: true,
      render: (s) => s.workspaceName ?? 'Unknown workspace',
    },
    {
      key: 'entityType',
      header: 'Type',
      width: '7rem',
      render: (s) => <span className="capitalize">{s.entityType}</span>,
    },
    {
      key: 'backupCount',
      header: 'Backups',
      width: '9rem',
      align: 'right',
      sortValue: (s) => s.backupCount,
      render: (s) => (
        <span>
          {s.backupCount}
          {hasWindow && (
            <Badge variant={s.pointsInRange > 0 ? 'success' : 'default'} className="ml-2">
              {s.pointsInRange} in range
            </Badge>
          )}
        </span>
      ),
    },
    {
      key: 'newest',
      header: 'Date range',
      width: '15rem',
      align: 'right',
      sortValue: (s) => s.newest ?? '',
      render: (s) => {
        const ago = daysAgo(s.newest);
        return s.backupCount > 0 ? (
          <span className="text-xs text-[var(--color-text-secondary)]">
            {fmtDate(s.oldest)} – {fmtDate(s.newest)}
            {ago != null && <> · newest {ago === 0 ? 'today' : `${ago}d ago`}</>}
          </span>
        ) : (
          <span className="text-xs text-[var(--color-text-secondary)]">no backups yet</span>
        );
      },
    },
  ];

  return (
    <div className="space-y-3">
      <FilterBar trailing={<span className="text-sm text-[var(--color-text-secondary)]">{rows.length} of {sets.length}</span>}>
        <div className="w-72">
          <Input
            inputSize="sm"
            isFullWidth
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, workspace, or type…"
          />
        </div>
        <div className="w-40">
          <Select selectSize="sm" isFullWidth value={type} onChange={(e) => setType(e.target.value)}>
            <option value="">All types</option>
            <option value="lakehouse">Lakehouse</option>
            <option value="warehouse">Warehouse</option>
          </Select>
        </div>
        <Input type="date" inputSize="sm" label="Backups from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" />
        <Input type="date" inputSize="sm" label="to" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" />
        <div className="flex items-center gap-1.5">
          <Button variant="ghost" size="sm" onClick={() => presetAgo(0, 7)}>7d</Button>
          <Button variant="ghost" size="sm" onClick={() => presetAgo(7, 14)}>7–14d</Button>
          <Button variant="ghost" size="sm" onClick={() => presetAgo(0, 30)}>30d</Button>
          {hasWindow && <Button variant="ghost" size="sm" onClick={clearWindow}>Clear</Button>}
        </div>
        {hasWindow && (
          <Checkbox checked={onlyInRange} onChange={(e) => setOnlyInRange(e.target.checked)} label="Only sets with a backup in range" />
        )}
      </FilterBar>

      {setsQ.isLoading ? (
        <LoadingState rows={4} />
      ) : setsQ.isError ? (
        <ErrorState onRetry={() => setsQ.refetch()} />
      ) : sets.length === 0 ? (
        <EmptyState
          title="No restorable backups yet"
          message="No lakehouse or warehouse has any recovery points to restore. Assign a storage destination and run a backup first."
        />
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          rowKey={(s) => `${s.entityType}:${s.entityId}`}
          onRowClick={(s) => onChange(s)}
          minWidth="48rem"
        />
      )}
    </div>
  );
};

export default BackupSetTable;
